import { GridItem, LayoutGrid, TypographyV3 } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { Ref } from 'react';
import { forwardRef } from 'react';

import Image from '@/app/components/Image';
import Motion from '@/app/components/Motion';

import HeroCopy from './HomeHeroCopy';
import HomeHeroShopChiclet from './HomeHeroShopChiclet';
import tValue from './HomeHeroTValue';

interface HomeTopHeroProps {
  readonly isBottomHeroInView: boolean;
}

const HomeTopHero = forwardRef(function HomeTopHero(
  { isBottomHeroInView }: HomeTopHeroProps,
  ref: Ref<HTMLDivElement>,
): JSX.Element {
  const t = useTranslations();

  const handleHeroButtonFocus = (
    event: React.FocusEvent<HTMLAnchorElement | HTMLButtonElement>,
    topHero: boolean | undefined,
  ) => {
    if (topHero) {
      event.preventDefault();
      event.stopPropagation();
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section
      className="h-lvh bg-sandstone-200 lg:px-5 lg:pb-4"
      data-cy="home-top-hero"
      ref={ref}
    >
      <LayoutGrid className="h-full">
        <GridItem
          className="h-full overflow-hidden lg:rounded-b-xl"
          colEnd={{ sm: 'full' }}
          colStart={{ sm: 'full' }}
          rowEnd={{ sm: 2 }}
          rowStart={{ sm: 1 }}
        >
          <Motion
            className="relative z-10 h-full md:hidden"
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.5, type: 'tween' }}
          >
            <Image
              alt={t('hero_top_img_alt')}
              className="object-cover"
              src={t('hero_top_image_mobile')}
              fill
              sizes="(max-width: 768px) 100vw"
              priority={true}
            />
          </Motion>
          <Motion
            className={cx('relative z-10 hidden h-full', 'md:block')}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.5, type: 'tween' }}
          >
            <Image
              alt={t('hero_top_img_alt')}
              className="object-cover"
              src={t('hero_top_image_desktop')}
              fill
              sizes="100vw"
              priority={true}
            />
          </Motion>
        </GridItem>
        <GridItem
          className="z-10 clip-polygon"
          colEnd={{ sm: 'full' }}
          colStart={{ sm: 'full' }}
          rowEnd={{ sm: 2 }}
          rowStart={{ sm: 1 }}
        >
          <div
            className={cx('left-0 top-0 h-lvh w-full lg:min-h-[50vw]', {
              fixed: isBottomHeroInView,
            })}
          >
            <LayoutGrid
              className="h-full content-between lg:px-5 lg:pb-4"
              rowGap={0}
            >
              <GridItem colEnd={{ sm: 'main' }} colStart={{ sm: 'main' }}>
                <Motion
                  initial={{
                    opacity: 0,
                    translateY: 25,
                    filter: 'blur(5px)',
                  }}
                  animate={{ opacity: 1, translateY: 0, filter: 'blur(0)' }}
                  transition={{ duration: 1.25, type: 'tween', delay: 1 }}
                >
                  <TypographyV3
                    Element="h1"
                    variant="h1"
                    className={cx(
                      'flex flex-col items-center pt-[22vh]',
                      'md:items-start md:pt-40',
                      'xl:w-11/12 xl:pt-[22vh]',
                    )}
                    color="light"
                  >
                    {t.rich('revolutionary_smart_ring_title', tValue)}
                  </TypographyV3>
                </Motion>
              </GridItem>
              <GridItem
                className="hidden md:block"
                colEnd={{ md: 10, lg: 10, xl: 8 }}
                colStart={{ md: 'main', lg: 'main', xl: 'main' }}
              >
                <Motion
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 2, type: 'tween', delay: 1.25 }}
                >
                  <HeroCopy topHero onHeroButtonFocus={handleHeroButtonFocus} />
                </Motion>
              </GridItem>
              <GridItem
                className="flex flex-col items-center self-end"
                colEnd={{ sm: 'main', md: 'main', lg: 'main', xl: 'main' }}
                colStart={{ sm: 'main', md: 15, lg: 15, xl: 18 }}
              >
                <Motion
                  className="md:hidden"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 2, type: 'tween', delay: 1.25 }}
                >
                  <HeroCopy topHero onHeroButtonFocus={handleHeroButtonFocus} />
                </Motion>
                <HomeHeroShopChiclet
                  topHero
                  onHeroButtonFocus={handleHeroButtonFocus}
                />
              </GridItem>
            </LayoutGrid>
          </div>
        </GridItem>
      </LayoutGrid>
    </section>
  );
});

export default HomeTopHero;
